import React, { useEffect, useRef, useState } from 'react';
import { Form, Select, Radio, Card, Slider, InputNumber, Row, Col } from 'antd';
import { observer } from 'mobx-react-lite';
import QuickTurnBoltStore from 'stores/QuickTurnBoltStore';
import MinAndMaxDimensions from '../MinAndMaxDimensions';

const Threads = () => {
    const {
        use_custom_thread,
        thread,
        threadSizes,
        custom_thread_major_diameter_inch,
        custom_thread_pitch_inch,
    } = QuickTurnBoltStore;

    const selectedThreadType = threadSizes?.find(x => x.id == thread)?.type || 'Imperial';

    const [units, setUnits] = useState<string>(selectedThreadType);

    const minMajorDiameterInch = () => {
        return Number((0.25).toFixed(3));
    };

    const maxMajorDiameterInch = () => {
        return Number((1).toFixed(3));
    };

    const minPitchInch = () => {
        return Number((0.015625).toFixed(3));
    };

    const maxPitchInch = () => {
        return Number((0.125).toFixed(3));
    };

    function handleChangeUseCustomThread(value: boolean): void {
        QuickTurnBoltStore.updateBoltById(QuickTurnBoltStore.boltToken, { use_custom_thread: value });
    }

    function handleChangeCustomThreadMajorDiameterInch(value: number): void {
        QuickTurnBoltStore.updateBoltById(QuickTurnBoltStore.boltToken, {
            custom_thread_major_diameter_inch: value,
        });
    }

    function handleChangeCustomThreadPitchInch(value: number): void {
        QuickTurnBoltStore.updateBoltById(QuickTurnBoltStore.boltToken, {
            custom_thread_pitch_inch: value,
        });
    }

    function handleChange(value: string): void {
        QuickTurnBoltStore.updateBoltById(QuickTurnBoltStore.boltToken, { thread: value });
    }

    return (
        <Form.Item label="Thread" className="mb-3">
            <Radio.Group
                value={use_custom_thread}
                options={[
                    { value: false, label: 'Standard' },
                    { value: true, label: 'Custom' },
                ]}
                className="mb-3"
                onChange={(e: any) => handleChangeUseCustomThread(e.target.value)}
            />

            {use_custom_thread ? (
                <>
                    <Form.Item label="Thread Major Diameter (in.)" className="mb-0">
                        <div className="d-flex flex-row align-items-center" style={{ columnGap: '20px' }}>
                            <div className="d-flex flex-column flex-grow-1">
                                <Slider
                                    className="w-100 m-0 my-2"
                                    min={minMajorDiameterInch()}
                                    max={maxMajorDiameterInch()}
                                    step={0.001}
                                    tooltip={{
                                        formatter: (value?: number) => `${value?.toFixed(3)}`,
                                    }}
                                    onChange={(value: number) => {
                                        QuickTurnBoltStore.setCustomThreadMajorDiameterInch(value);
                                    }}
                                    onAfterChange={(value: number) => {
                                        QuickTurnBoltStore.setCustomThreadMajorDiameterInch(value);
                                        handleChangeCustomThreadMajorDiameterInch(value);
                                    }}
                                    value={custom_thread_major_diameter_inch}
                                />
                                <MinAndMaxDimensions min={minPitchInch()} max={maxPitchInch()} unit="inch" />
                            </div>
                            <InputNumber
                                min={minMajorDiameterInch()}
                                max={maxMajorDiameterInch()}
                                value={custom_thread_major_diameter_inch}
                                precision={3}
                                step={0.001}
                                onChange={value => {
                                    if (value) {
                                        QuickTurnBoltStore.setCustomThreadMajorDiameterInch(value);
                                        handleChangeCustomThreadMajorDiameterInch(value);
                                    }
                                }}
                                style={{ width: '100px' }}
                                className="LoNotSensitive"
                            />
                        </div>
                    </Form.Item>
                    <Form.Item label="Thread Pitch (in.)" className="mb-0">
                        <div className="d-flex flex-row align-items-center" style={{ columnGap: '20px' }}>
                            <div className="d-flex flex-column flex-grow-1">
                                <Slider
                                    className="w-100 m-0 my-2"
                                    min={minPitchInch()}
                                    max={maxPitchInch()}
                                    step={0.001}
                                    tooltip={{
                                        formatter: (value?: number) => `${value?.toFixed(3)}`,
                                    }}
                                    onChange={(value: number) => {
                                        QuickTurnBoltStore.setCustomThreadPitchInch(value);
                                    }}
                                    onAfterChange={(value: number) => {
                                        QuickTurnBoltStore.setCustomThreadPitchInch(value);
                                        handleChangeCustomThreadPitchInch(value);
                                    }}
                                    value={custom_thread_pitch_inch}
                                />
                                <MinAndMaxDimensions min={minPitchInch()} max={maxPitchInch()} unit="inch" />
                            </div>
                            <InputNumber
                                min={minPitchInch()}
                                max={maxPitchInch()}
                                value={custom_thread_pitch_inch}
                                precision={3}
                                step={0.001}
                                onChange={value => {
                                    if (value) {
                                        QuickTurnBoltStore.setCustomThreadPitchInch(value);
                                        handleChangeCustomThreadPitchInch(value);
                                    }
                                }}
                                style={{ width: '100px' }}
                                className="LoNotSensitive"
                            />
                        </div>
                    </Form.Item>
                </>
            ) : (
                <Form.Item label="Thread Size" className="mb-0">
                    <Row style={{ alignItems: 'baseline' }}>
                        <Col lg={12} md={8} sm={8} span={12}>
                            <Radio.Group
                                value={units}
                                options={[
                                    { value: 'Imperial', label: 'Imperial' },
                                    { value: 'Metric', label: 'Metric' },
                                ]}
                                onChange={(e: any) => setUnits(e.target.value)}
                            />
                        </Col>
                        <Col lg={12} md={16} sm={16} span={12}>
                            <Select
                                options={threadSizes
                                    ?.filter(x => x.type.toLowerCase() === units.toLowerCase())
                                    ?.map(x => {
                                        return {
                                            value: x?.id,
                                            label: (
                                                <div className="d-flex justify-content-between">
                                                    <div>{x?.display_name}</div>
                                                    {/* <div>{x?.pitch_type}</div> */}
                                                </div>
                                            ),
                                        };
                                    })}
                                value={thread}
                                onChange={(value: string) => {
                                    QuickTurnBoltStore.setThread(value);
                                    handleChange(value);
                                }}
                            />
                        </Col>
                    </Row>
                </Form.Item>
            )}
        </Form.Item>
    );
};

export default observer(Threads);
